import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, CircularProgress, Popper } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { fetchRooms, checkRoomConflicts } from "../api/rooms";

const TC = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)", surface2: "var(--color-surface-press-light)", surface3: "var(--color-surface-press-light)",
  orange: "#C2410C", orange10: "rgba(194,65,12,.10)",
};

function FeatureChips({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", mt: "4px" }}>
      {items.map((f) => (
        <Box key={f} sx={{ fontSize: "10.5px", color: TC.ink3, px: "6px", py: "1px", borderRadius: "5px", background: TC.surface3 }}>{f}</Box>
      ))}
    </Box>
  );
}

/**
 * Props:
 *   value            - currently selected roomId (number | null)
 *   onChange         - (roomId: number | null) => void
 *   date             - ISO date string "YYYY-MM-DD" (required for conflict check)
 *   startTime        - "HH:MM" (required for conflict check)
 *   endTime          - "HH:MM" (required for conflict check)
 *   excludeSessionId - session being edited (skip self in conflict check)
 *   disabled         - bool
 *   siteId           - only rooms at this site are offered (optional)
 *   minCapacity      - class seats; only rooms that hold this many are offered (rooms with no capacity set stay)
 */
export default function RoomPicker({
  value, onChange, date, startTime, endTime, excludeSessionId, disabled, minCapacity, siteId,
}) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tSecondary = theme.palette.secondary.main;
  const tPrimary10 = alpha(tPrimary, 0.08);
  const [rooms, setRooms]         = useState([]);
  const [conflicts, setConflicts] = useState({});
  const [loading, setLoading]     = useState(false);
  const [open, setOpen]           = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    fetchRooms().then(setRooms).catch(() => {});
  }, []);

  useEffect(() => {
    if (!date || !startTime || !endTime || rooms.length === 0) return;
    const checks = rooms.map((r) =>
      checkRoomConflicts(r.id, { date, startTime, endTime, excludeSessionId })
        .then((list) => [r.id, list.length > 0])
        .catch(() => [r.id, false])
    );
    setLoading(true);
    Promise.all(checks)
      .then((results) => {
        const map = {};
        results.forEach(([id, hasConflict]) => { map[id] = hasConflict; });
        setConflicts(map);
      })
      .finally(() => setLoading(false));
  }, [date, startTime, endTime, rooms, excludeSessionId]);

  const selected = rooms.find((r) => r.id === value);
  const fits = (r) => (!minCapacity || r.capacity == null || r.capacity >= minCapacity)
    && (!siteId || r.siteId == null || String(r.siteId) === String(siteId));
  const offered = rooms.filter(fits);

  // Seats went up past the chosen room: drop the room rather than keep an over-full booking.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selected && !fits(selected)) onChange(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minCapacity, siteId, selected]);

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        component="button"
        ref={anchorRef}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        sx={{
          display: "flex", alignItems: "center", gap: "8px",
          width: "100%", px: "12px", py: "9px",
          border: `1.5px solid ${TC.line}`, borderRadius: "9px",
          background: "#fff", cursor: disabled ? "default" : "pointer",
          fontFamily: "inherit", fontSize: "13.5px", color: TC.ink,
          "&:hover:not(:disabled)": { borderColor: TC.ink3 },
        }}
      >
        <MeetingRoomIcon sx={{ fontSize: 16, color: TC.ink4 }} />
        <Box sx={{ flex: 1, textAlign: "left" }}>
          {selected ? selected.name : <span style={{ color: TC.ink4 }}>No room assigned</span>}
        </Box>
        {loading && <CircularProgress size={12} />}
        {value && conflicts[value] && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "11px", color: TC.orange }}>
            <WarningAmberIcon sx={{ fontSize: 13 }} /> Conflict
          </Box>
        )}
      </Box>
      {selected && !open && <FeatureChips items={selected.equipment} />}

      {/* Popper renders in a portal so a parent card/panel with overflow:hidden can't clip the list. */}
      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" style={{ zIndex: 1500 }}
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}>
        <>
          <Box sx={{ position: "fixed", inset: 0, zIndex: -1 }} onClick={() => setOpen(false)} />
          <Box sx={{
            width: anchorRef.current?.offsetWidth || undefined, minWidth: 220,
            background: "#fff", border: `1.5px solid ${TC.line}`, borderRadius: "10px",
            boxShadow: "0 8px 24px -6px rgba(20,24,40,.18)",
            maxHeight: 320, overflowY: "auto",
          }}>
            <Box
              component="button"
              onClick={() => { onChange(null); setOpen(false); }}
              sx={{
                display: "flex", alignItems: "center", gap: "8px", width: "100%",
                px: "12px", py: "9px", border: "none", background: "transparent",
                cursor: "pointer", fontFamily: "inherit", fontSize: "13px",
                color: TC.ink3, borderBottom: `1px solid ${TC.line}`,
                "&:hover": { background: TC.surface2 },
              }}
            >
              No room
            </Box>

            {offered.length === 0 && (
              <Typography sx={{ px: 2, py: 1.5, fontSize: "13px", color: TC.ink4 }}>
                {rooms.length === 0 ? "No rooms configured yet" : `No rooms hold ${minCapacity} seats`}
              </Typography>
            )}

            {offered.map((room) => {
              const isSelected  = room.id === value;
              const hasConflict = conflicts[room.id];
              return (
                <Box
                  key={room.id}
                  component="button"
                  onClick={() => { onChange(room.id); setOpen(false); }}
                  sx={{
                    display: "flex", alignItems: "center", gap: "8px", width: "100%",
                    px: "12px", py: "9px", border: "none",
                    background: isSelected ? tPrimary10 : hasConflict ? TC.orange10 : "transparent",
                    cursor: "pointer", fontFamily: "inherit",
                    borderBottom: `1px solid ${TC.line}`,
                    "&:last-child": { borderBottom: "none" },
                    "&:hover": { background: isSelected ? tPrimary10 : hasConflict ? TC.orange10 : TC.surface3 },
                  }}
                >
                  <Box sx={{
                    width: 28, height: 28, borderRadius: "7px", flexShrink: 0,
                    display: "grid", placeItems: "center",
                    background: isSelected ? tPrimary10 : TC.surface2,
                    color: isSelected ? tPrimary : TC.ink4,
                  }}>
                    <MeetingRoomIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box sx={{ flex: 1, textAlign: "left" }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: TC.ink }}>
                      {room.name}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: TC.ink3 }}>
                      {room.type} · {room.capacity} cap
                    </Typography>
                    <FeatureChips items={room.equipment} />
                  </Box>
                  {isSelected && <CheckIcon sx={{ fontSize: 14, color: tPrimary }} />}
                  {hasConflict && !isSelected && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "10.5px", color: TC.orange, fontWeight: 700 }}>
                      <WarningAmberIcon sx={{ fontSize: 12 }} /> Busy
                    </Box>
                  )}
                  {!hasConflict && !isSelected && date && (
                    <Box sx={{ fontSize: "10.5px", color: tSecondary, fontWeight: 700 }}>Free</Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      </Popper>
    </Box>
  );
}
